// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the ⋯ menu on every Konvo card in the Assistant chat (design/assistant-dm-cleanup r5-menu):
// History, Pin to the top, Mute this kind, Archive. On desktop the menu opens right under the ⋯ that
// was pressed, aligned to its right edge; on a phone it is a bottom sheet. AssistantCardFrame puts
// the ⋯ at a card's top-right (shown on hover, always on touch) and tells the card it is in the chat.

import {AssistantCardLiveLabel} from '@app/features/konvo_assistant/components/chat_chrome/AssistantCardLiveLabel';
import styles from '@app/features/konvo_assistant/components/chat_chrome/AssistantCardMenu.module.css';
import {AssistantCardContext} from '@app/features/konvo_assistant/components/chat_chrome/AssistantChatChromeContext';
import {
	ARCHIVE,
	CARD_OPTIONS,
	CARD_UPDATE_FAILED,
	HISTORY,
	HISTORY_TODAY,
	KONVO_REPLY,
	muteLabel,
	PIN_TO_TOP,
	UNARCHIVE,
	UNPIN,
} from '@app/features/konvo_assistant/components/chat_chrome/AssistantChatChromeMessages';
import {
	type AssistantCardMenuSpec,
	cardMenuSpec,
} from '@app/features/konvo_assistant/components/chat_chrome/AssistantChatChromeModel';
import {
	AssistantChatChrome,
	archiveWithUndo,
} from '@app/features/konvo_assistant/components/chat_chrome/AssistantChatChromeState';
import AssistantChat from '@app/features/konvo_assistant/state/AssistantChat';
import type {Message} from '@app/features/messaging/models/MessagingMessage';
import {DataMenuRenderer} from '@app/features/ui/action_menu/DataMenuRenderer';
import * as ContextMenuCommands from '@app/features/ui/commands/ContextMenuCommands';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {useContextMenuTrigger} from '@app/features/ui/hooks/useContextMenuTrigger';
import {
	ArchiveIcon,
	BellIcon,
	BellSlashIcon,
	ClockCounterClockwiseIcon,
	DotsThreeIcon,
	PushPinIcon,
	PushPinSlashIcon,
	TrayIcon,
} from '@app/features/ui/icons/Icons';
import {MenuBottomSheet, type MenuGroupType} from '@app/features/ui/menu_bottom_sheet/MenuBottomSheet';
import MobileLayout from '@app/features/ui/state/MobileLayout';
import {Tooltip} from '@app/features/ui/tooltip/Tooltip';
import type {AssistantChatKind} from '@fluxer/schema/src/domains/konvo/AssistantChat';
import type {I18n} from '@lingui/core';
import {useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback, useLayoutEffect, useMemo, useRef, useState} from 'react';

const ICON_SIZE = 18;

export interface AssistantCardMenuActions {
	onHistory: () => void;
	onPin: (pinned: boolean) => void;
	onMute: (kind: AssistantChatKind, muted: boolean) => void;
	onArchive: (archived: boolean) => void;
}

/** The menu's groups: History, Pin, Mute (mutable kinds only), then Archive on its own. */
export function assistantCardMenuGroups(
	i18n: I18n,
	spec: AssistantCardMenuSpec,
	todayCount: number | null,
	actions: AssistantCardMenuActions,
	close: () => void,
): Array<MenuGroupType> {
	const run = (action: () => void) => () => {
		close();
		action();
	};
	const first: MenuGroupType['items'] = [];
	if (spec.kind) {
		first.push({
			id: 'history',
			label: i18n._(HISTORY),
			icon: <ClockCounterClockwiseIcon size={ICON_SIZE} />,
			hint: todayCount ? i18n._(HISTORY_TODAY, {count: todayCount}) : undefined,
			onClick: run(actions.onHistory),
		});
	}
	first.push({
		id: 'pin',
		label: i18n._(spec.pinned ? UNPIN : PIN_TO_TOP),
		icon: spec.pinned ? <PushPinSlashIcon size={ICON_SIZE} /> : <PushPinIcon size={ICON_SIZE} />,
		onClick: run(() => actions.onPin(!spec.pinned)),
	});
	const mute = spec.mute;
	if (mute) {
		first.push({
			id: 'mute',
			label: muteLabel(i18n, mute.kind, mute.muted),
			icon: mute.muted ? <BellIcon size={ICON_SIZE} /> : <BellSlashIcon size={ICON_SIZE} />,
			onClick: run(() => actions.onMute(mute.kind, !mute.muted)),
		});
	}
	return [
		{items: first},
		{
			items: [
				{
					id: 'archive',
					label: i18n._(spec.archived ? UNARCHIVE : ARCHIVE),
					icon: spec.archived ? <TrayIcon size={ICON_SIZE} /> : <ArchiveIcon size={ICON_SIZE} />,
					onClick: run(() => actions.onArchive(!spec.archived)),
				},
			],
		},
	];
}

function isArchived(messageId: string): boolean {
	if (AssistantChat.archivedIds.has(messageId)) return true;
	return (AssistantChat.state?.conversations ?? []).some(
		(conversation) => conversation.archived && conversation.message_ids.includes(messageId),
	);
}

/** Pin, mute and archive for one card, with a notice when the server says no. */
function useCardActions(message: Message, kind: AssistantChatKind | null): AssistantCardMenuActions {
	const {i18n} = useLingui();
	return useMemo(() => cardActions(i18n, message, kind), [i18n, message.id, kind]);
}

function cardActions(i18n: I18n, message: Message, kind: AssistantChatKind | null): AssistantCardMenuActions {
	return {
		onHistory: () => AssistantChat.openHistory({messageId: message.id, kind: kind ?? 'other'}),
		onPin: (pinned) => {
			void AssistantChat.setPinned(message.id, pinned).catch(() => ToastCommands.error(i18n._(CARD_UPDATE_FAILED)));
		},
		onMute: (muteKind, muted) => {
			void AssistantChat.setMuted(muteKind, muted).catch(() => ToastCommands.error(i18n._(CARD_UPDATE_FAILED)));
		},
		onArchive: (archived) => {
			if (archived) void archiveWithUndo(i18n, {messageIds: [message.id]}, 'card');
			else
				void AssistantChat.setArchived({messageIds: [message.id]}, false).catch(() =>
					ToastCommands.error(i18n._(CARD_UPDATE_FAILED)),
				);
		},
	};
}

/**
 * The chat's card actions (History, Pin, Mute, Archive) as plain menu entries, for a card in the
 * Assistant chat that has a ⋯ of its own (dashboards): one menu holds both, so the card shows one ⋯.
 */
export function assistantCardMenuEntries(
	i18n: I18n,
	message: Message,
	kind: AssistantChatKind | null,
): Array<Array<{id: string; label: string; icon?: React.ReactNode; onClick: () => void}>> {
	const spec = cardMenuSpec(kind, {
		pinned: AssistantChat.pinnedIds.includes(message.id),
		archived: isArchived(message.id),
		muted: AssistantChat.isMuted(kind),
	});
	const groups = assistantCardMenuGroups(
		i18n,
		spec,
		AssistantChatChrome.todayCount(kind),
		cardActions(i18n, message, kind),
		() => undefined,
	);
	return groups.map((group) =>
		group.items.flatMap((item) =>
			'onClick' in item && item.onClick
				? [{id: item.id ?? item.label, label: item.label, icon: item.icon, onClick: () => item.onClick()}]
				: [],
		),
	);
}

function cardTitle(message: Message, fallback: string): string {
	const embed = message.embeds.find((candidate) => candidate.title || candidate.description);
	return embed?.title?.trim() || embed?.description?.split('\n')[0]?.trim() || fallback;
}

interface AssistantCardMenuButtonProps {
	message: Message;
	kind: AssistantChatKind | null;
	/** Inline in a card header (the Agents card) instead of floating at the card's corner. */
	inline?: boolean;
	className?: string;
}

export const AssistantCardMenuButton = observer(({message, kind, inline, className}: AssistantCardMenuButtonProps) => {
	const {i18n} = useLingui();
	const {isOpen, withTracking} = useContextMenuTrigger();
	const [sheetOpen, setSheetOpen] = useState(false);
	const actions = useCardActions(message, kind);
	const spec = () =>
		cardMenuSpec(kind, {
			pinned: AssistantChat.pinnedIds.includes(message.id),
			archived: isArchived(message.id),
			muted: AssistantChat.isMuted(kind),
		});
	const prefetch = useCallback(() => {
		if (kind === 'agents') void AssistantChatChrome.prefetchHistoryCount('agents');
	}, [kind]);
	const open = (event: React.MouseEvent<HTMLButtonElement>) => {
		event.preventDefault();
		event.stopPropagation();
		prefetch();
		if (MobileLayout.isMobileLayout()) {
			setSheetOpen(true);
			return;
		}
		ContextMenuCommands.openFromElementBottomRight(
			event,
			({onClose}) => (
				<DataMenuRendererLive message={message} kind={kind} spec={spec} actions={actions} onClose={onClose} />
			),
			withTracking(),
		);
	};
	const label = i18n._(CARD_OPTIONS);
	return (
		<>
			<Tooltip text={label} position="bottom" data-flx="konvo-assistant.card-menu.tooltip">
				<button
					type="button"
					className={clsx(styles.button, inline ? styles.inline : styles.corner, isOpen && styles.open, className)}
					aria-label={label}
					aria-haspopup="menu"
					aria-expanded={isOpen || sheetOpen}
					onClick={open}
					onPointerEnter={prefetch}
					data-testid="assistant-card-menu-button"
					data-flx="konvo-assistant.card-menu.button"
				>
					<DotsThreeIcon size={18} weight="bold" />
				</button>
			</Tooltip>
			{sheetOpen && (
				<MenuBottomSheet
					isOpen
					onClose={() => setSheetOpen(false)}
					title={cardTitle(message, i18n._(KONVO_REPLY))}
					groups={assistantCardMenuGroups(i18n, spec(), AssistantChatChrome.todayCount(kind), actions, () =>
						setSheetOpen(false),
					)}
					data-flx="konvo-assistant.card-menu.sheet"
				/>
			)}
		</>
	);
});

/** The desktop menu, observing the store so "N today" appears once the History count arrives. */
const DataMenuRendererLive = observer(
	({
		kind,
		spec,
		actions,
		onClose,
	}: {
		message: Message;
		kind: AssistantChatKind | null;
		spec: () => AssistantCardMenuSpec;
		actions: AssistantCardMenuActions;
		onClose: () => void;
	}) => {
		const {i18n} = useLingui();
		return (
			<div className={styles.menu} data-flx="konvo-assistant.card-menu.menu">
				<DataMenuRenderer
					groups={assistantCardMenuGroups(i18n, spec(), AssistantChatChrome.todayCount(kind), actions, onClose)}
				/>
			</div>
		);
	},
);

interface AssistantCardFrameProps {
	message: Message;
	kind: AssistantChatKind | null;
	/** False when the card draws the ⋯ itself (the Agents card puts it in its header). */
	cornerButton: boolean;
	children: React.ReactNode;
}

/**
 * A card's first row: the header that opens it (action, timeline, dashboard cards; also as the
 * first block of a structured reply) or the legacy card's source row. Mirrors the CSS selectors.
 */
function firstRow(frame: HTMLElement): HTMLElement | null {
	const root = frame.firstElementChild;
	return (
		root?.querySelector<HTMLElement>(':scope > header:first-child') ??
		root?.firstElementChild?.querySelector<HTMLElement>(':scope > header:first-child') ??
		frame.querySelector<HTMLElement>('[data-flx="channel.embeds.konvo-card.source"]')
	);
}
const BUTTON_SIZE = 28;
const EDGE_TOP = 8;
const SURFACE_ATTR = 'data-card-menu-surface';

/**
 * The card's own box (the first element with an outline, shadow or fill that spans the card), so
 * the room for the ⋯ goes inside it and the card still ends where every other card ends.
 */
function cardSurface(frame: HTMLElement): HTMLElement | null {
	const width = frame.getBoundingClientRect().width;
	const walker = document.createTreeWalker(frame, NodeFilter.SHOW_ELEMENT);
	for (let node = walker.nextNode(); node; node = walker.nextNode()) {
		const element = node as HTMLElement;
		if (element.getBoundingClientRect().width < width * 0.8) continue;
		const style = getComputedStyle(element);
		const outlined = style.borderTopStyle !== 'none' && style.borderTopWidth !== '0px';
		const filled = style.backgroundColor !== 'rgba(0, 0, 0, 0)' && style.backgroundColor !== 'transparent';
		if (outlined || filled || style.boxShadow !== 'none') return element;
	}
	return null;
}

/**
 * Seats the ⋯ in room of its own: the card's first row gets space reserved at its end (the
 * `data-card-menu="row"` CSS) and the button is centred on that row; a card without such a row
 * reserves the space along its right edge instead (`data-card-menu="edge"`).
 */
function useMenuSeat(enabled: boolean) {
	const ref = useRef<HTMLDivElement>(null);
	const [seat, setSeat] = useState<{mode: 'row' | 'edge'; top: number}>({mode: 'edge', top: EDGE_TOP});
	useLayoutEffect(() => {
		const frame = ref.current;
		if (!enabled || !frame) return;
		const measure = () => {
			const row = firstRow(frame);
			for (const marked of frame.querySelectorAll(`[${SURFACE_ATTR}]`)) marked.removeAttribute(SURFACE_ATTR);
			if (!row) {
				cardSurface(frame)?.setAttribute(SURFACE_ATTR, '');
				setSeat((previous) =>
					previous.mode === 'edge' && previous.top === EDGE_TOP ? previous : {mode: 'edge', top: EDGE_TOP},
				);
				return;
			}
			const box = frame.getBoundingClientRect();
			const rect = row.getBoundingClientRect();
			const top = Math.max(4, Math.round(rect.top - box.top + (rect.height - BUTTON_SIZE) / 2));
			setSeat((previous) => (previous.mode === 'row' && previous.top === top ? previous : {mode: 'row', top}));
		};
		measure();
		if (typeof ResizeObserver === 'undefined') return;
		const observer = new ResizeObserver(measure);
		observer.observe(frame);
		return () => {
			observer.disconnect();
			for (const marked of frame.querySelectorAll(`[${SURFACE_ATTR}]`)) marked.removeAttribute(SURFACE_ATTR);
		};
	}, [enabled]);
	return {ref, seat};
}

/** Wraps a Konvo card in the Assistant chat: its ⋯ at the top-right, and the card context. */
export function AssistantCardFrame({message, kind, cornerButton, children}: AssistantCardFrameProps) {
	const scope = useMemo(
		() => ({
			message,
			kind,
			menuButton: <AssistantCardMenuButton message={message} kind={kind} inline />,
			liveLabel: kind === 'agents' ? <AssistantCardLiveLabel message={message} /> : null,
		}),
		[message, kind],
	);
	const {ref, seat} = useMenuSeat(cornerButton);
	return (
		<AssistantCardContext.Provider value={scope}>
			<div
				ref={ref}
				className={styles.frame}
				data-assistant-card={message.id}
				data-card-menu={cornerButton ? seat.mode : undefined}
				style={cornerButton ? ({'--card-menu-top': `${seat.top}px`} as React.CSSProperties) : undefined}
				data-flx="konvo-assistant.card-menu.frame"
			>
				{children}
				{cornerButton && <AssistantCardMenuButton message={message} kind={kind} />}
			</div>
		</AssistantCardContext.Provider>
	);
}
